// Message Item Component
// Displays an individual chat message with citations, images, and rating

import { useState, useCallback, useEffect } from 'react';
import { ExternalLink, FileText, Download, ExternalLink as OpenIcon, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useChatStore } from '@/stores/chatStore';
import { createComponentLogger } from '@/utils/logger';
import { formatFileSize } from '@/utils/imageProcessing';
import { MarkdownContent } from './MarkdownContent';
import { ActionRail } from './ActionRail';
import { cn } from '@/lib/utils';
import type { AIMessage, ImageAttachment, CitationMetadata } from '@/types/chat';

const log = createComponentLogger('MessageItem', 'ai-chat');

// ============================================================================
// Component Props
// ============================================================================

interface MessageItemProps {
  message: AIMessage;
  onViewDocument?: (documentId: string) => void;
  /** Callback when a citation chip is clicked (opens sources panel) */
  onSelectCitation?: (citation: CitationMetadata) => void;
  /** Callback when regenerate is clicked */
  onRegenerate?: () => void;
  /** Whether regenerate is allowed (disabled for Wisr agent) */
  allowRegenerate?: boolean;
}

// ============================================================================
// Main Component
// ============================================================================

export function MessageItem({
  message,
  onViewDocument,
  onSelectCitation,
  onRegenerate,
  allowRegenerate = true
}: MessageItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ImageAttachment | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const { rateMessage, flagMessage } = useChatStore();

  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const attachments = message.metadata?.attachments || [];
  const citations = message.metadata?.citations || [];
  const hasSources = citations.length > 0 || (message.documents_used && message.documents_used.length > 0);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleRate = async (rating: number) => {
    log.info('Rating message', { messageId: message.id, rating });
    await rateMessage(message.id, rating);
  };

  const handleFlag = async () => {
    log.info('Flagging message', { messageId: message.id });
    const reason = prompt('Please provide a reason for flagging this message:');
    if (reason) {
      await flagMessage(message.id, reason);
    }
  };

  const handleViewDocument = (docId: string) => {
    log.debug('Viewing document', { documentId: docId });
    if (onViewDocument) {
      onViewDocument(docId);
    }
  };

  const handleCitationClick = (citation: CitationMetadata) => {
    log.debug('Citation clicked', {
      documentId: citation.document_id,
      title: citation.title,
      page: citation.page_start
    });
    if (onSelectCitation) {
      onSelectCitation(citation);
    }
  };

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(message.content);
    log.debug('Message content copied', { messageId: message.id });
  }, [message.content, message.id]);

  const handleViewSources = useCallback(() => {
    // If we have citations, select the first one to open the sources panel
    if (citations.length > 0 && onSelectCitation) {
      onSelectCitation(citations[0]);
    }
  }, [citations, onSelectCitation]);

  const handleRegenerate = useCallback(() => {
    if (onRegenerate) {
      log.info('Regenerating response', { messageId: message.id });
      onRegenerate();
    }
  }, [onRegenerate, message.id]);

  useEffect(() => {
    if (typeof window === 'undefined') {
return;
}
    const mql = window.matchMedia('(hover: none)');
    const update = () => setIsTouchDevice(mql.matches);

    update();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, []);

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const renderImages = () => {
    if (attachments.length === 0) {
      return null;
    }

    return (
      <div className="mb-3">
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className="relative group cursor-pointer"
              onClick={() => setSelectedImage(attachment)}
            >
              <img
                src={attachment.url}
                alt={attachment.filename}
                className="h-32 w-32 object-cover rounded-lg border border-border/50 hover:border-primary transition-all hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 rounded-b flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="truncate flex-1">{attachment.filename}</span>
                <span className="ml-1">{formatFileSize(attachment.size)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper to format page range
  const formatPageRange = (pageStart: number | null, pageEnd: number | null): string => {
    if (pageStart === null) {
return '';
}
    if (pageEnd === null || pageEnd === pageStart) {
return `p. ${pageStart}`;
}
    return `pp. ${pageStart}-${pageEnd}`;
  };

  const renderDocumentCitations = () => {
    // Use new citation metadata if available, fall back to documents_used
    if (citations.length === 0 && (!message.documents_used || message.documents_used.length === 0)) {
      return null;
    }

    // If we have rich citations, show them
    if (citations.length > 0) {
      const topCitations = citations.slice(0, 3);
      const remainingCitations = citations.slice(3);

      return (
        <div className="mt-3 pt-3 border-t border-border/30">
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">
                  {citations.length} source{citations.length !== 1 ? 's' : ''}
                </span>
              </div>
              {remainingCitations.length > 0 && (
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                    <ChevronDown className={`h-3 w-3 mr-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    {isExpanded ? 'Hide' : `+${remainingCitations.length} more`}
                  </Button>
                </CollapsibleTrigger>
              )}
            </div>

            {/* Top 3 sources - always visible */}
            <div className="space-y-1.5 mt-2">
              {topCitations.map((citation, index) => {
                const isHighRelevance = citation.relevance > 0.5;
                const pageRange = formatPageRange(citation.page_start, citation.page_end);

                return (
                  <TooltipProvider key={citation.chunk_id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleCitationClick(citation)}
                          className="w-full flex items-start gap-2 p-2 bg-muted/40 hover:bg-muted/70 rounded-lg text-xs transition-all text-left group"
                        >
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              <span className="font-medium truncate">{citation.title}</span>
                              {isHighRelevance && (
                                <span className="text-green-600 text-[10px]">●</span>
                              )}
                            </div>
                            {pageRange && (
                              <span className="text-muted-foreground text-[10px]">{pageRange}</span>
                            )}
                          </div>
                          <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs">
                        <p className="text-xs line-clamp-2">{citation.preview}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {Math.round(citation.relevance * 100)}% relevance
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>

            {/* Remaining sources - collapsible */}
            <CollapsibleContent>
              <div className="space-y-1.5 mt-1.5">
                {remainingCitations.map((citation, index) => {
                  const isHighRelevance = citation.relevance > 0.5;
                  const pageRange = formatPageRange(citation.page_start, citation.page_end);

                  return (
                    <TooltipProvider key={citation.chunk_id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleCitationClick(citation)}
                            className="w-full flex items-start gap-2 p-2 bg-muted/40 hover:bg-muted/70 rounded-lg text-xs transition-all text-left group"
                          >
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium">
                              {index + 4}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium truncate">{citation.title}</span>
                                {isHighRelevance && (
                                  <span className="text-green-600 text-[10px]">●</span>
                                )}
                              </div>
                              {pageRange && (
                                <span className="text-muted-foreground text-[10px]">{pageRange}</span>
                              )}
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs">
                          <p className="text-xs line-clamp-2">{citation.preview}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {Math.round(citation.relevance * 100)}% relevance
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      );
    }

    // Fallback: Legacy documents_used display (for old messages without rich citations)
    const topSources = message.documents_used!.slice(0, 3);
    const remainingSources = message.documents_used!.slice(3);

    return (
      <div className="mt-3 pt-3 border-t border-border/30">
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">
                {message.documents_used!.length} source{message.documents_used!.length !== 1 ? 's' : ''}
              </span>
            </div>
            {remainingSources.length > 0 && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                  <ChevronDown className={`h-3 w-3 mr-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  {isExpanded ? 'Hide' : `+${remainingSources.length} more`}
                </Button>
              </CollapsibleTrigger>
            )}
          </div>

          {/* Top 3 sources - always visible */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {topSources.map((docId, index) => {
              const score = message.retrieval_scores?.[docId];
              const isHighRelevance = score && score > 0.25;
              return (
                <TooltipProvider key={docId}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleViewDocument(docId)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-muted/50 hover:bg-muted rounded text-xs transition-colors"
                      >
                        <span className="font-medium">[{index + 1}]</span>
                        {isHighRelevance && (
                          <span className="text-green-600 text-[10px]">●</span>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        {score ? `${(score * 100).toFixed(0)}% relevance` : 'View source'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>

          {/* Remaining sources - collapsible */}
          <CollapsibleContent>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {remainingSources.map((docId, index) => {
                const score = message.retrieval_scores?.[docId];
                const isHighRelevance = score && score > 0.25;
                return (
                  <TooltipProvider key={docId}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleViewDocument(docId)}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-muted/50 hover:bg-muted rounded text-xs transition-colors"
                        >
                          <span className="font-medium">[{index + 4}]</span>
                          {isHighRelevance && (
                            <span className="text-green-600 text-[10px]">●</span>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">
                          {score ? `${(score * 100).toFixed(0)}% relevance` : 'View source'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  };

  const actionRailClassName = isTouchDevice
    ? 'absolute -bottom-4 left-4 z-10 opacity-100'
    : 'absolute -bottom-4 left-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200';


  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div
      className={cn(
        'group relative flex gap-3 mb-6',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(event) => {
        if (!isAssistant || !isTouchDevice) {
return;
}
        const target = event.target as HTMLElement;
        // Guard against interactive elements: buttons, links, images, code blocks, etc.
        if (target.closest('button, a, img, pre, code, [data-interactive], [role="button"], .citation-chip')) {
return;
}
        setShowActions((prev) => !prev);
      }}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        )}
      >
        {isUser ? 'You' : 'AI'}
      </div>

      {/* Message Content */}
      <div className={cn('flex-1 max-w-[75%]', isUser ? 'items-end' : 'items-start')}>
        <div
          className={cn(
            'relative p-4 rounded-xl border shadow-sm transition-all hover:shadow-md',
            isUser
              ? 'bg-primary text-primary-foreground hover:shadow-primary/20 border-primary/20'
              : 'bg-gradient-to-br from-muted/80 to-muted/60 hover:from-muted/90 hover:to-muted/70 border-border/50'
          )}
        >
          {/* Image Attachments */}
          {renderImages()}

          {/* Message Text */}
          {isUser ? (
            <div className="whitespace-pre-wrap break-words">{message.content}</div>
          ) : (
            <MarkdownContent content={message.content} />
          )}

          {/* Document Citations (Assistant only) */}
          {isAssistant && renderDocumentCitations()}

          {/* Action Rail - appears on hover for assistant messages */}
          {isAssistant && (isTouchDevice ? showActions : isHovered) && (
            <ActionRail
              className={actionRailClassName}
              userRating={message.user_rating}
              isFlagged={message.flagged}
              hasSources={hasSources}
              allowRegenerate={allowRegenerate}
              onCopy={handleCopy}
              onRegenerate={handleRegenerate}
              onViewSources={handleViewSources}
              onRate={handleRate}
              onFlag={handleFlag}
            />
          )}
        </div>

        {/* Timestamp */}
        <div className="flex items-center gap-2 mt-2 px-1">
          <span className="text-xs text-muted-foreground">
            {formatTime(message.created_at)}
          </span>
        </div>
      </div>

      {/* Image Lightbox Dialog */}
      {selectedImage && (
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span className="truncate">{selectedImage.filename}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{formatFileSize(selectedImage.size)}</Badge>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          className="h-8 w-8"
                        >
                          <a
                            href={selectedImage.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenIcon className="h-4 w-4" />
                          </a>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Open in new tab</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          className="h-8 w-8"
                        >
                          <a
                            href={selectedImage.url}
                            download={selectedImage.filename}
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Download</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center p-4">
              <img
                src={selectedImage.url}
                alt={selectedImage.filename}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
            <div className="text-xs text-muted-foreground text-center">
              {selectedImage.dimensions && (
                <span>
                  {selectedImage.dimensions.width} × {selectedImage.dimensions.height} px
                </span>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
